import React, { FC } from "react";
import "./CustomSelectInput.scss";
import Select, {
  FormatOptionLabelMeta,
  GroupBase,
  OptionProps,
  Options,
  Props,
  StylesConfig,
  SelectComponentsConfig,
} from "react-select";
import { objectToClassString } from "../../utils/functions";
import makeAnimated from "react-select/animated";
import AsyncCreatableSelect from "react-select/async-creatable";
import AsyncSelect from "react-select/async";
import type { AsyncProps } from "react-select/async";
import type { CreatableProps } from "react-select/creatable";
import Typhography from "../Typhography/Typhography";
import classNames from "classnames";
import { CheckIcon } from "@phosphor-icons/react";

const animatedComponents = makeAnimated();

export interface ICustomSelectInput {
  options?: Props["options"];
  label?: string;
  onChange: (newValue: any) => void;
  originValue?: boolean;
  cacheOptions?: boolean;
  value: any;
  isMulti?: boolean;
  isDisabled?: boolean;
  isOptionSelected?:
    | ((option: any, selectValue: Options<any>) => boolean)
    | undefined;
  clearOption?: {
    label: string;
    value?: any;
  };
  autoHeight?: boolean;
  className?: string;
  containerClassName?: string;
  isLoading?: boolean;
  menuPosition?: "absolute" | "fixed";
  menuPortalTarget?: HTMLElement | null;
  formatOptionLabel?:
    | ((
        data: unknown,
        formatOptionLabelMeta: FormatOptionLabelMeta<unknown>,
      ) => React.ReactNode)
    | undefined;
  isCreatable?: boolean;
  placeholder?: string;
  formatCreateLabel?: CreatableProps<
    unknown,
    boolean,
    GroupBase<any>
  >["formatCreateLabel"];
  components?: SelectComponentsConfig<any, boolean, GroupBase<unknown>>;
  loadOptions?: AsyncProps<unknown, boolean, GroupBase<any>>["loadOptions"];
  onCreateOption?: CreatableProps<
    any,
    boolean,
    GroupBase<any>
  >["onCreateOption"];
  defaultOptions?: AsyncProps<
    unknown,
    boolean,
    GroupBase<any>
  >["defaultOptions"];
  defaultValue?: Props<any>["defaultValue"];
}

const getCustomStyles = (autoHeight: boolean): StylesConfig<any> => ({
  option: (provided, state) => ({
    ...provided,
    // color: state.isSelected ? 'red' : 'blue',
    backgroundColor: state.isSelected ? "#ECEFFD" : "white",
    lineHeight: "1.5rem",
    fontWeight: "700",
    color: "#3A3878",
    padding: 10,
    "&:hover": {
      backgroundColor: "#ECEFFD",
    },
  }),
  indicatorSeparator: (base, props) => ({
    display: "none",
  }),
  menu: (style, state) => ({
    ...style,
    borderRadius: "0.5rem",
    border: "1px solid #F2F2F2",
    boxShadow: "0 6px 24px rgba(0,0,0,0.08)",
    backgroundColor: "white",
    padding: "0.5rem",
  }),
  // 12010 è la fascia che `index.scss` riserva ai menu e ai popover Chakra,
  // cioè a ciò che deve stare sopra una Dialog (12002). Con il vecchio 9999 il
  // menu finiva DIETRO la modal: si vedeva solo la parte che ne sporgeva, e
  // sembrava che la select non si aprisse.
  menuPortal: (base) => ({ ...base, zIndex: 12010 }),
  control: (provided) => ({
    ...provided,
    border: "1px solid #E7E7E7",
    width: "100%",
    borderRadius: "0.5rem",
    height: autoHeight ? "auto" : "40px",
    minHeight: "40px",
    cursor: "pointer",
    boxShadow: "none",
    backgroundColor: "#FBFBFB",
    minWidth: 150,
    "&:focus-visible": {
      outline: "none",
      boxShadow: "none",
    },
    "&:hover": {
      border: "1px solid #03212c",
    },
  }),
  input: (provided) => ({
    ...provided,
    color: "#3A3878",
    "& input": {
      fontFamily: "Satoshi",
      fontSize: "1rem",
      fontStyle: "normal",
      fontWeight: "500",
      lineHeight: "1.25rem",
      color: "#00212C",
    },
  }),
  singleValue: (provided, state) => {
    const opacity = state.isDisabled ? 0.5 : 1;
    const transition = "opacity 300ms";
    const props = {
      color: "#00212C",
      fontFamily: "Satoshi",
      fontSize: " 0.875rem",
      fontStyle: "normal",
      fontWeight: "500",
      lineHeight: "1rem",
    };

    return { ...provided, opacity, transition, ...props };
  },
});
const CustomOption: React.ComponentType<
  OptionProps<any, boolean, GroupBase<any>>
> = ({ innerProps, isDisabled, isSelected, label }) =>
  !isDisabled ? (
    <div
      className={`container__custom__select__input__option ${objectToClassString(
        { not__selected: !isSelected },
      )}`}
      {...innerProps}
    >
      {isSelected && <CheckIcon />}
      <span className="label text-medium-16">{label}</span>
    </div>
  ) : null;

const CustomSelectInput: FC<ICustomSelectInput> = ({
  onChange,
  value,
  options: optionsProp,
  clearOption,
  isMulti,
  isCreatable,
  cacheOptions,
  isDisabled = false,
  className = "",
  autoHeight = false,
  formatOptionLabel,
  isOptionSelected,
  formatCreateLabel = (value) => `Inserisci '${value}'`,
  loadOptions,
  isLoading,
  onCreateOption,
  components = {},
  containerClassName,
  menuPosition,
  menuPortalTarget,
  originValue,
  defaultOptions,
  defaultValue,
  label,
  placeholder = "Inserisci per cercare...",
}) => {
  const options = clearOption
    ? [
        {
          label: clearOption.label,
          value: clearOption.value,
        },
        ...(optionsProp as any),
      ]
    : optionsProp;
  if (isCreatable) {
    return (
      <AsyncCreatableSelect
        className={`custom__select__input ${className}`}
        classNamePrefix="select"
        placeholder={placeholder}
        noOptionsMessage={() => <p>Nessuna opzione</p>}
        styles={getCustomStyles(autoHeight)}
        cacheOptions={cacheOptions}
        isLoading={isLoading}
        isDisabled={isDisabled}
        formatCreateLabel={formatCreateLabel}
        value={value}
        defaultValue={defaultValue}
        menuPosition={menuPosition}
        onCreateOption={onCreateOption}
        loadOptions={loadOptions}
        defaultOptions={defaultOptions}
        isMulti={isMulti}
        formatOptionLabel={formatOptionLabel}
        components={{
          ...animatedComponents,
          Option: CustomOption,
          ...components,
        }}
        onChange={(newValue: any) => {
          onChange(newValue);
        }}
        options={options}
      />
    );
  }
  if (loadOptions) {
    return (
      <div className="d-flex flex-column">
        {label && (
          <Typhography size="bold-14" type="span" className="label-input">
            {label}
          </Typhography>
        )}
        <AsyncSelect
          className={classNames(`custom__select__input ${className}`, {
            disabled: isDisabled,
          })}
          classNamePrefix="select"
          placeholder={placeholder}
          noOptionsMessage={() => <p>Nessuna opzione</p>}
          styles={getCustomStyles(autoHeight)}
          cacheOptions={cacheOptions}
          isDisabled={isDisabled}
          menuPosition={menuPosition}
          value={value}
          isOptionSelected={isOptionSelected}
          defaultValue={defaultValue}
          loadOptions={loadOptions}
          defaultOptions={defaultOptions}
          isMulti={isMulti}
          isLoading={isLoading}
          formatOptionLabel={formatOptionLabel}
          components={{
            ...animatedComponents,
            Option: CustomOption,
            ...components,
          }}
          onChange={(newValue: any) => {
            onChange(newValue);
          }}
          options={options}
        />
      </div>
    );
  }

  const stringify = (data: any) =>
    typeof data === "string" ? data : JSON.stringify(data);

  const getSelectedValue = () => {
    if (originValue) return value;
    if (value === null) return value;
    if (isMulti)
      return options?.filter((option) => value.includes((option as any).value));
    return options?.find(
      (option) => stringify((option as any).value) === stringify(value),
    );
  };

  return (
    <div className={`d-flex flex-column  ${containerClassName}`}>
      {label && (
        <Typhography size="bold-14" type="span" className="label-input">
          {label}
        </Typhography>
      )}
      <Select
        className={classNames(`custom__select__input ${className}`, {
          disabled: isDisabled,
        })}
        classNamePrefix="select"
        placeholder={placeholder}
        noOptionsMessage={() => <p>Nessuna opzione</p>}
        styles={getCustomStyles(autoHeight)}
        value={getSelectedValue()}
        formatOptionLabel={formatOptionLabel}
        isMulti={isMulti}
        isDisabled={isDisabled}
        isOptionSelected={isOptionSelected}
        menuPosition={menuPosition}
        menuPortalTarget={menuPortalTarget}
        components={{ ...animatedComponents, Option: CustomOption, ...components }}
        onChange={(newValue: any) => {
          onChange(
            isMulti ? newValue.map((item: any) => item.value) : newValue.value,
          );
        }}
        options={options}
      />
    </div>
  );
};

export default CustomSelectInput;
